import { useEffect, useMemo, useRef, useState } from "react";
import useToggleRow from "../../../hooks/toggle-row.hook";
import visibilityUtils from "../../../Utils/visibility.utils";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import CategoryBody from "./category-body.item";
import { CallToast } from "../../PriceCalculator/modals/CustomToast";
import { CategoriesColorSequence } from "../../../constants/category.constants";
import SearchTitle from "../../stack/toolset-selection/browse-by-technology/update-tool-modal/search-title.component";

const CategoryItem = ({ 
  category, index, categories, setCategories, search = "", showDragHandle = false,
  categoryIsRemovingId,
  isForStorefront = false,
  onRemove = () => {},
  onDuplicate = () => {},
  onViewProducts = () => {},
  onCategoryUpdate = () => {},
  handleSubCategoryDragEnd = () => {},
  isView = false,
}) => {
  const [subDraggingId, setSubDraggingId] = useState(null);
  const rowRef = useRef(null);
  const { panelRef, toggleRow, isOpenFixed, refreshState, setOpen, closeAllPanels } = useToggleRow({ rowRef, panelVisibleOut: true });
  const hasSubCategories = category.subCategories?.length > 0;

  function handleSubcategoriesDragEnd(result) {
    if (isView) return;
    const resultList = visibilityUtils.handleDragEnd(result, category.subCategories);
    const updatedCategories = [...categories];
    updatedCategories[index].subCategories = resultList;
    setCategories(updatedCategories);
    handleSubCategoryDragEnd(resultList);
  }

  const sortedSubCategories = useMemo(() => {
    return category.subCategories?.sort((a, b) => a.SortOrder - b.SortOrder);
  }, [category.subCategories]);

  const hide = useMemo(() => {
    if (!search) return false;
    const nameMatch = SearchTitle({ title: category.categoryName, searchValue: search });
    if (nameMatch) return false;
    if (category.subCategories) {
      const subMatch = category.subCategories.some(sub => {
        return SearchTitle({ title: sub.categoryName, searchValue: search });
      });
      return !subMatch;
    }
    return true;
  }, [search]);

  const categoryColorValidation = (color) => {
    const isColorUsed = categories.some(
      (cat) => cat.categoryId !== category.categoryId && cat.categoryHexColor === color
    );
    if (isColorUsed) {
      CallToast("This color is already in use", false, false, "v2style");
    }
    return !isColorUsed;
  }

  const subCategoryColorValidation = (color) => {
    const isColorUsed = categories[index]?.subCategories?.some((subCategory) => subCategory.categoryHexColor === color);
    if (isColorUsed) {
      CallToast("This color is already in use", false, false, "v2style");
    }
    return !isColorUsed;
  }

  const getNewCategoryColor = () => {
    const colorFromList = CategoriesColorSequence?.find((color) => {
      return !categories[index]?.subCategories?.some((category) => {
        return category?.categoryHexColor?.toLowerCase() === color?.toLowerCase();
      });
    });
    if (!colorFromList) {
      return "#000000";
    }
    return colorFromList;
  }

  const handleAddSubcategoryClick = () => {
    const updatedCategories = [...categories];
    const categoryId = `custom-${Math.random()}`;
    const theLowestSortOrder = Math.min(...categories.map(c => c.sortOrder)) || 0;
    updatedCategories[index].subCategories = [
      ...(updatedCategories[index].subCategories ?? []),
      {
        categoryId,
        categoryName: "New Sub Category",
        categoryHexColor: getNewCategoryColor(),
        SortOrder: theLowestSortOrder - 1,
        Visibility: true,
        isEditing: true,
        isCustom: true,
        newCreated: true,
        parentCategoryId: category.categoryId,
        storefrontActive: true,
        techSpendActive: true,
      }
    ];
    setCategories(updatedCategories);
    setTimeout(() => {
      const input = document.getElementById(`category-name-inp-${categoryId}`);
      input.focus();
    }, 100);
  }

  useEffect(() => {
    refreshState();
  }, [category?.subCategories?.length])

  useEffect(() => {
    if (!search) {
      closeAllPanels();
      return;
    }
    setOpen(true);
  }, [search]);

  if (hide) return null;

  const isHidden = isForStorefront && !category.storefrontActive;

  return (
    <Draggable
      key={category?.categoryId}
      draggableId={`${category?.categoryId}`}
      index={index}
      isDragDisabled={isView}
    >
      {(provided) => {
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className="category-item-wrapper"
          >
            <div ref={rowRef} className="point category-item">
              <CategoryBody 
                category={category} 
                hasSubCategories={hasSubCategories} 
                toggleRow={toggleRow}
                isOpen={isOpenFixed}
                index={index}
                categories={categories}
                setCategories={setCategories}
                onViewProducts={onViewProducts}
                onRemove={onRemove}
                onDuplicate={onDuplicate}
                showDragHandle={showDragHandle}
                dragHandleProps={provided.dragHandleProps}
                search={search}
                onCategoryUpdate={(category) => {
                  onCategoryUpdate(null, category, "Category");
                }}
                colorValidation={categoryColorValidation}
                categoryIsRemovingId={categoryIsRemovingId}
                isForStorefront={isForStorefront}
                isHidden={isHidden}
                isView={isView}
              />
              <div ref={panelRef} className="panel">
                <DragDropContext 
                  onDragEnd={(result) => {
                    if (isView) return;
                    handleSubcategoriesDragEnd(result);
                    setSubDraggingId(null);
                  }}
                  onDragStart={(start) => {
                    setSubDraggingId(start.draggableId);
                  }}
                  isDropDisabled={false}
                  isCombineEnabled={false}
                >
                  <Droppable
                    direction="vertical"
                    droppableId="droppableSection"
                    isDropDisabled={isView}
                    isCombineEnabled={false}
                  >
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        isDropDisabled={isView}
                        ref={provided.innerRef}
                      >
                        <div className="subcategory-body" onClick={(e) => e.stopPropagation()}>
                          {hasSubCategories && 
                          sortedSubCategories
                          ?.map((subCategory, subIndex) => {
                            const isSubHidden = isHidden || (isForStorefront && !subCategory.storefrontActive);
                            return (
                              <div key={`subcat-${subCategory.categoryId}`}>
                                <Draggable
                                  draggableId={`${subCategory?.categoryId}`}
                                  index={subIndex}
                                  isDropDisabled={false}
                                  isDragDisabled={isView}
                                >
                                  {(subProvided) => (
                                     <div
                                      ref={subProvided.innerRef}
                                      {...subProvided.draggableProps}
                                      className="subcategory-item-wrapper"
                                     >
                                      <CategoryBody
                                        category={subCategory}
                                        isChild
                                        index={subIndex}
                                        categories={category.subCategories}
                                        setCategories={(updatedSubCategories) => {
                                          const updatedCategories = [...categories];
                                          updatedCategories[index].subCategories = updatedSubCategories;
                                          setCategories(updatedCategories);
                                        }}
                                        colorValidation={subCategoryColorValidation}
                                        parentIndex={index}
                                        showDragHandle={showDragHandle}
                                        onRemove={onRemove}
                                        onDuplicate={onDuplicate}
                                        dragHandleProps={subProvided.dragHandleProps}
                                        search={search}
                                        onViewProducts={onViewProducts}
                                        onCategoryUpdate={(updatedSubCategory) => {
                                          const updatedCategories = [...categories];
                                          updatedCategories[index].subCategories = updatedCategories[index].subCategories.map(sub => sub.categoryId === updatedSubCategory.categoryId ? updatedSubCategory : sub);
                                          onCategoryUpdate(updatedCategories[index], updatedSubCategory, "Subcategory");
                                        }}
                                        categoryIsRemovingId={categoryIsRemovingId}
                                        isHidden={isSubHidden}
                                        isView={isView}
                                      />
                                     </div>
                                  )}
                                </Draggable>
                              </div>
                            )
                          })}
                          {subDraggingId && (
                            <div 
                              style={{ height: '48px' }}
                            />
                          )}
                          {!isView && (
                            <div 
                              className={`p-1 f-12 line-height-18 d-flex align-items-center w-fit-content f-500 f-grey pointer gap-8 ${!hasSubCategories ? 'pt-3' : ''} ${isHidden ? 'faded' : ''}`}
                              onClick={() => {
                                handleAddSubcategoryClick();
                              }}
                            >
                              <div
                                className="d-flex justify-content-center align-items-center faded-child"
                                style={{ width: '12px' }}
                              >
                                <img
                                  alt=""
                                  src="/images/actioncenter/plusgrey.svg"
                                  width={9}
                                />
                              </div>
                              <span className="faded-child">
                                Add subcategory
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </div>
          </div>
        )
      }}
    </Draggable>
  )
}

export default CategoryItem;
